/**
 * Level derivation from lifetime XP (R7.4/R7.11). Pure function of
 * (xpTotal, LevelCurve) — no I/O, no log access (ADR-0001 rule 2).
 *
 * `LevelCurve.thresholds` is the cumulative XP required to REACH level i+2
 * (level 1 starts at 0 XP implicitly — reward-config.ts's own doc comment).
 * Beyond the last threshold, each further level costs the last increment
 * again, so an edited curve (R7.9) can never "run out" of levels.
 */

import type { LevelCurve, LevelIdentity } from '../../contracts';

export interface LevelDerivation {
  /** Levels start at 1 (contract invariant). */
  readonly level: number;
  /** XP earned inside the current level (>= 0, < xpRequiredForNextLevel unless the curve has no further increment, i.e. never — see below). */
  readonly xpIntoCurrentLevel: number;
  /** XP span of the current level — denominator for the progress bar (R7.8). */
  readonly xpRequiredForNextLevel: number;
}

/**
 * Cumulative XP threshold to REACH a given level (1-indexed). Level 1 is 0
 * by construction. Beyond `thresholds.length + 1`, extrapolates using the
 * last defined increment (curve "never runs out" rule, reward-config.ts).
 */
export function thresholdForLevel(level: number, curve: LevelCurve): number {
  if (level <= 1) return 0;
  const index = level - 2; // thresholds[0] = XP to reach level 2
  if (index < curve.thresholds.length) {
    return curve.thresholds[index] ?? 0;
  }
  // Beyond the defined curve: extrapolate using the last increment,
  // repeated for every level past the last defined threshold.
  const lastThreshold = curve.thresholds[curve.thresholds.length - 1] ?? 0;
  const secondLastThreshold =
    curve.thresholds[curve.thresholds.length - 2] ?? 0;
  const lastIncrement = curve.thresholds.length >= 2
    ? lastThreshold - secondLastThreshold
    : lastThreshold; // single-threshold curve: reuse that one increment
  const levelsBeyond = index - curve.thresholds.length + 1;
  return lastThreshold + lastIncrement * levelsBeyond;
}

/**
 * Derive current level + progress-to-next from lifetime XP. Deterministic,
 * total (handles xpTotal === 0, a curve with zero thresholds, and XP far
 * beyond the last defined threshold via extrapolation).
 */
export function deriveLevel(
  xpTotal: number,
  curve: LevelCurve,
): LevelDerivation {
  let level = 1;
  // Walk forward while the NEXT level's threshold is already reached.
  // Bounded by construction: thresholds are strictly increasing (R7.9
  // guardrail enforced by the settings editor), and the loop only advances
  // while xpTotal >= thresholdForLevel(level + 1, curve), which strictly
  // grows with level once past the defined curve (extrapolation always adds
  // a positive increment) — so this always terminates.
  while (xpTotal >= thresholdForLevel(level + 1, curve)) {
    level += 1;
  }

  const currentLevelThreshold = thresholdForLevel(level, curve);
  const nextLevelThreshold = thresholdForLevel(level + 1, curve);

  return {
    level,
    xpIntoCurrentLevel: xpTotal - currentLevelThreshold,
    xpRequiredForNextLevel: nextLevelThreshold - currentLevelThreshold,
  };
}

/**
 * Identity (name/icon) for a given level (R7.5/OQ11). Levels beyond the
 * defined `identities` array reuse the last entry verbatim, mirroring the
 * curve's own "reuse the last increment" rule (reward-config.ts doc
 * comment) — the arc has a top, and staying there is itself the
 * achievement (settings-rewards.md §1.2).
 */
export function identityForLevel(
  level: number,
  identities: readonly LevelIdentity[],
): LevelIdentity {
  const exact = identities.find((identity) => identity.level === level);
  if (exact) return exact;

  // Beyond the defined arc: reuse the highest-numbered identity, restamped
  // with the requested level number (so the UI can still show "Level 14"
  // alongside "Master Navigator" per settings-rewards.md's closing caption).
  const last = identities.reduce<LevelIdentity | null>((max, identity) => {
    if (!max || identity.level > max.level) return identity;
    return max;
  }, null);

  if (!last) {
    // Only reachable with a malformed (empty) identities array; fall back to
    // a safe, honest default rather than throwing — this is a display
    // concern, not a correctness-critical one, and an empty array is a
    // contract-violation the settings editor's floor-of-one-level guardrail
    // is supposed to prevent (settings-rewards.md §1.3).
    return { level, name: `Level ${level}`, iconId: 'circle' };
  }
  return { level, name: last.name, iconId: last.iconId };
}
